import { useEffect, useState, Suspense } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { authService } from '../services/auth.service';
import { useAuthStore } from '../stores/auth.store';
import { Sidebar } from './Sidebar';
import { Loader2, Menu } from 'lucide-react';
import { Button } from './ui/button';
import { useRouteRefresh } from '@/hooks/useRouteRefresh';
import logoNubipos from '../assets/logo-nuvi.png';

export function DashboardLayout() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user, setUser, logout } = useAuthStore();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const location = useLocation();

    // Cerrar sidebar móvil al navegar
    useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

    // Forzar refetch de datos al navegar entre rutas del sidebar
    useRouteRefresh();

    // Obtener perfil del usuario al montar el componente
    const { data: profile } = useQuery({
        queryKey: ['profile'],
        queryFn: authService.getProfile,
        retry: false,
        staleTime: 0, // Siempre considerar los datos como stale
    });

    useEffect(() => {
        if (profile) {
            setUser(profile);
        }
    }, [profile, setUser]);

    const handleLogout = async () => {
        try {
            await authService.logout();
            // Limpiar el cache de React Query para que al hacer login con otro usuario se cargue su perfil
            queryClient.clear();
            logout();
            toast.success('Sesión cerrada correctamente');
            navigate('/login', { replace: true });
        } catch {
            // Incluso si falla en el servidor, cerramos sesión localmente
            queryClient.clear();
            logout();
            navigate('/login', { replace: true });
        }
    };

    const toggleSidebar = () => {
        setSidebarCollapsed(!sidebarCollapsed);
    };

    return (
        <div className="flex h-dvh overflow-hidden bg-background">
            {/* Overlay mobile */}
            {mobileNavOpen && (
                <div
                    className="fixed inset-0 z-30 bg-black/50 lg:hidden"
                    onClick={() => setMobileNavOpen(false)}
                />
            )}

            {/* Sidebar — drawer en mobile, fijo en desktop */}
            <div className={`
                fixed inset-y-0 left-0 z-40 lg:relative lg:z-auto
                transition-transform duration-300 ease-in-out
                ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                <Sidebar
                    user={user}
                    onLogout={handleLogout}
                    collapsed={sidebarCollapsed}
                    onToggle={toggleSidebar}
                />
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                {/* Top bar mobile */}
                <div className="lg:hidden flex items-center gap-3 px-4 py-3 border-b bg-card flex-shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => setMobileNavOpen(true)}>
                        <Menu className="h-5 w-5" />
                    </Button>
                    <img src={logoNubipos} alt="NubiPOS" className="h-6 w-auto" />
                </div>

                <main className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6 xl:p-8">
                    <div className="max-w-9xl mx-auto">
                        <Suspense fallback={
                            <div className="flex h-full w-full items-center justify-center p-12">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            </div>
                        }>
                            <Outlet />
                        </Suspense>
                    </div>
                </main>
            </div>
        </div>
    );
}
